import React, { useEffect, useRef, useState } from 'react';

import { Button } from '@repo/videa-components/src/index';
import { useAtom, useAtomValue } from 'jotai';
import { ChevronDown, Mic } from 'lucide-react';

import {
  hasMicrophonePermissionAtom,
  isDeviceSelectionOpenAtom,
  isRecorderSheetOpenAtom,
} from '../../atoms';
import { useAudioDeviceManager } from '../permissions';

interface MicrophoneSelectorProps {
  onMonitoringStreamChange?: (stream: MediaStream | null) => void;
}

const MicrophoneSelector: React.FC<MicrophoneSelectorProps> = ({
  onMonitoringStreamChange,
}) => {
  const [isOpen, setIsOpen] = useAtom(isDeviceSelectionOpenAtom);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const hasMicrophonePermission = useAtomValue(hasMicrophonePermissionAtom);
  const [monitoringStream, setMonitoringStream] = useState<MediaStream | null>(
    null,
  );
  const isSheetOpen = useAtomValue(isRecorderSheetOpenAtom);

  const { availableDevices, selectedDeviceId, isLoading, error, selectDevice } =
    useAudioDeviceManager();

  const selectedDevice = availableDevices.find(
    (device) => device.deviceId === selectedDeviceId,
  );

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);

  const handleDeviceSelect = (deviceId: string) => {
    selectDevice(deviceId);
    setIsOpen(false);
  };

  const startMicrophoneMonitoring = async () => {
    try {
      setIsMonitoring(true);

      // Get audio stream with selected device
      const constraints = selectedDeviceId
        ? { audio: { deviceId: { exact: selectedDeviceId } } }
        : { audio: true };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setMonitoringStream(stream);
      onMonitoringStreamChange?.(stream);

      // Set up audio analysis for the VoiceCard
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
    } catch (err) {
      console.error('Failed to start microphone monitoring:', err);
      setIsMonitoring(false);
    }
  };

  const stopMicrophoneMonitoring = () => {
    setIsMonitoring(false);

    if (monitoringStream) {
      monitoringStream.getTracks().forEach((track) => track.stop());
      setMonitoringStream(null);
      onMonitoringStreamChange?.(null);
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
  };

  // Start monitoring when device is selected and sheet is open
  useEffect(() => {
    if (selectedDeviceId && isSheetOpen && !isMonitoring) {
      startMicrophoneMonitoring();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies: 'startMicrophoneMonitoring' and 'stopMicrophoneMonitoring'. Either include them or remove the dependency array. */
  }, [selectedDeviceId, isSheetOpen, isMonitoring]);

  // Stop monitoring when sheet is closed
  useEffect(() => {
    if (!isSheetOpen && isMonitoring) {
      stopMicrophoneMonitoring();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'stopMicrophoneMonitoring'. Either include it or remove the dependency array. */
  }, [isSheetOpen, isMonitoring]);

  // Clear monitoring stream atom when sheet is closed
  useEffect(() => {
    if (!isSheetOpen) {
      // Stop any active monitoring stream
      if (monitoringStream) {
        monitoringStream.getTracks().forEach((track) => track.stop());
      }
      onMonitoringStreamChange?.(null);
    }
  }, [isSheetOpen, onMonitoringStreamChange, monitoringStream]);

  // Handle device changes: stop current stream, then start new one
  useEffect(() => {
    if (selectedDeviceId && isSheetOpen && isMonitoring) {
      stopMicrophoneMonitoring();
      // Start new stream after stopping current one
      startMicrophoneMonitoring();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'stopMicrophoneMonitoring'. Either include it or remove the dependency array. */
  }, [selectedDeviceId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMicrophoneMonitoring();
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'stopMicrophoneMonitoring'. Either include it or remove the dependency array. */
  }, []);

  if (error) {
    return (
      <div className="vai-flex vai-items-center vai-gap-2 vai-p-2 vai-bg-red-50 vai-border vai-border-red-200 vai-rounded-md">
        <Mic className="vai-w-4 vai-h-4 vai-text-red-500" />
        <span className="vai-text-sm vai-text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div className="vai-space-y-3">
      <div className="vai-relative">
        <Button
          variant="outline"
          onClick={() => setIsOpen(!isOpen)}
          disabled={isLoading || availableDevices.length === 0}
          className="vai-flex vai-items-center vai-gap-2 vai-w-full vai-justify-between vai-font-normal"
        >
          <div className="vai-flex vai-items-center vai-gap-2 vai-min-w-0 vai-flex-1">
            <Mic className="vai-w-4 vai-h-4 vai-flex-shrink-0" />
            <span className="vai-text-sm vai-truncate vai-min-w-0">
              {!hasMicrophonePermission
                ? 'None'
                : isLoading
                  ? 'Loading microphones...'
                  : selectedDevice?.label || 'Select microphone'}
            </span>
          </div>
          <ChevronDown
            className={`vai-w-4 vai-h-4 vai-transition-transform ${isOpen ? 'vai-rotate-180' : ''}`}
          />
        </Button>

        {isOpen && (
          <div className="vai-absolute vai-top-full vai-left-0 vai-right-0 vai-mt-1 vai-bg-white vai-border vai-border-gray-200 vai-rounded-md vai-shadow-lg vai-z-50 vai-max-h-60 vai-overflow-y-auto">
            {!hasMicrophonePermission ? (
              <div className="vai-p-3 vai-text-sm vai-text-gray-500 vai-text-center">
                Microphone permission required
              </div>
            ) : availableDevices.length === 0 ? (
              <div className="vai-p-3 vai-text-sm vai-text-gray-500 vai-text-center">
                No microphones found
              </div>
            ) : (
              <>
                <div className="vai-p-2 vai-border-b vai-border-gray-200">
                  <span className="vai-text-sm">Select Microphone</span>
                </div>
                {availableDevices.map((device) => (
                  <button
                    key={device.deviceId}
                    onClick={() => handleDeviceSelect(device.deviceId)}
                    className={`vai-w-full vai-px-3 vai-py-2 vai-text-left vai-text-sm hover:vai-bg-gray-50 vai-flex vai-items-center vai-gap-2 ${
                      device.deviceId === selectedDeviceId
                        ? 'vai-bg-blue-50 vai-text-blue-700'
                        : 'vai-text-gray-700'
                    }`}
                  >
                    <Mic className="vai-w-4 vai-h-4 vai-flex-shrink-0" />
                    <span className="vai-truncate">{device.label}</span>
                    {device.deviceId === selectedDeviceId && (
                      <div className="vai-w-2 vai-h-2 vai-bg-blue-500 vai-rounded-full vai-ml-auto" />
                    )}
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MicrophoneSelector;
